import type {
  BookingResponse,
  Leg,
  Pax,
  EmergencyContact,
  IncludedExtra,
} from '@models/landing/api/api-response.model';
import { PassengerTypeEnum, SsrType, StatusType } from '@models/landing/ui/select-passenger-model';
import type { Airport, CodeTypeItem, MealsList } from '@models/master-data/master-data.model';
import type {
  CheckedInPax,
  NotCheckedInPax,
  NotCheckedInPaxFlight,
  NotCheckedInPaxFlightLeg,
  CheckedInPaxLeg,
  EmergencyContactDetails,
} from '@models/ui/pnr-data-model';
import { getClassValue } from '@utils/flight-util';
import { mapApiApisInfoToUi } from './apis-info.mapper';
import { getCountryOptionsWithSelected } from './contact-details.mapper';
import { selectCountries, useMasterDataStore } from '@store/master-data.store';
import {
  canSelectForCheckIn,
  countAllowCheckIn,
  countCheckedInFlights,
  countStandByFlights,
  isCheckedInStatus,
  isStandByStatus,
  resolveLeftBorderColor,
  resolveStandByID90,
  resolveStandByWaitlist,
} from '@helper/passenger-status.util';
import { formatPassengerName } from './passenger-name.util';

/** Resource key shown when MASD is opted-in without a confirmed timeslot. */
const ASSIST_INCLUDED_TEXT_KEY = 'Review.IncludedExtras.Text';

const mapEmergencyContactToDetails = (
  ec: EmergencyContact,
  countryList: ReturnType<typeof selectCountries>,
): EmergencyContactDetails => {
  const { selectedOption, options } = getCountryOptionsWithSelected(
    countryList,
    ec.countryCode ?? '',
  );
  return {
    contactName: ec.contactName,
    contactRelation: ec.contactRelation,
    contactNumber: ec.contactNumber,
    countryCode: ec.countryCode,
    isDisclosed: ec.isDisclosed,
    contactType: ec.contactType,
    contactId: ec.contactId,
    emergencyContactId: ec.emergencyContactId,
    selected: selectedOption,
    countryOption: options,
  };
};

// Helper to parse slot date string (from meet-and-assist mapper)
const parseSlotDate = (dateStr: string): Date => {
  const iso = new Date(dateStr);
  if (!Number.isNaN(iso.getTime())) return iso;
  // Handle "d/M/yyyy HH:mm:ss" format returned by the API
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/.exec(dateStr);
  if (match) {
    const [, day, month, year, hours, minutes, seconds] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds),
    );
  }
  return new Date(NaN);
};

// Helper to format assist time slot as "04:00 - 04:30"
const formatAssistTimeSlot = (slotFrom: string, slotTo: string): string => {
  const fromDate = parseSlotDate(slotFrom);
  const toDate = parseSlotDate(slotTo);

  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return `${slotFrom} - ${slotTo}`;
  }

  const fromTime = fromDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const toTime = toDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return `${fromTime} - ${toTime}`;
};

// =======================
// 🔹 Checked-In Passengers Mapper
// =======================

// Resolve code name from codeTypeList by codeID
export const resolveCodeName = (
  codeType: string | null,
  codeTypeList: CodeTypeItem[],
): string | null => {
  if (codeType === null) {
    return null;
  }
  const shortCodeName =
    codeTypeList.find((item) => item.codeID === codeType)?.shortCodeName ?? null;
  const mobileShortCodeName =
    codeTypeList.find((item) => item.codeID === codeType)?.mobileShortCodeName ?? null;
  return shortCodeName?.includes('+') ? mobileShortCodeName : shortCodeName;
};

const getMealName = (
  codeType: string | null,
  mealList: MealsList,
  codeTypeList: CodeTypeItem[],
): string | null => {
  if (codeType === null) {
    return null;
  }

  const mealNameFromMealsList = mealList.find((item) => item.code === codeType)?.name;
  if (mealNameFromMealsList) {
    return mealNameFromMealsList;
  }

  return resolveCodeName(codeType, codeTypeList);
};

// Get SSR value with proper resolution based on SSR type
const getSsrValue = (
  ssrType: string,
  ssr: { amount: number; row: string | null; column: string | null; codeType: string | null },
  codeTypeList: CodeTypeItem[],
  mealList?: MealsList,
): string => {
  if (ssrType.toLocaleLowerCase() === SsrType.SEAT.toLocaleLowerCase()) {
    return ssr.row && ssr.column ? `${ssr.row}${ssr.column}` : '';
  } else if (ssrType.toLocaleLowerCase() === SsrType.MEAL.toLocaleLowerCase()) {
    return getMealName(ssr.codeType, mealList ?? [], codeTypeList) ?? '';
  } else {
    return resolveCodeName(ssr.codeType, codeTypeList) ?? ssr.codeType ?? '';
  }
};

const getExtraByType = (pax: Pax, ssrType: SsrType): Pax['includedExtras'][number] | undefined =>
  pax.includedExtras.find(
    (extra) => extra.ssrType.toLocaleLowerCase() === ssrType.toLocaleLowerCase(),
  );

interface CheckedBaggageSsrFlags {
  isPurchased?: boolean | string;
  amount?: number | null;
  isIncluded?: boolean;
  codeType?: string | null;
  ssrType?: string;
}

const isCheckedBaggageSsrType = (ssrType: string): boolean =>
  ssrType.toLocaleLowerCase() === SsrType.CHECKEDBAGGAGE.toLocaleLowerCase();

const isPurchasedCheckedBaggage = (ssr: CheckedBaggageSsrFlags): boolean =>
  ssr.isPurchased === true || ssr.isPurchased === 'true';

/**
 * Additional checked baggage:
 * - includedExtras: `isPurchased === true` only
 * - selectedSSRList often omits `isPurchased`; treat paid extras (`amount > 0` or
 *   `isIncluded === false`) as additional so they do not replace the base bag
 */
const isAdditionalCheckedBaggage = (
  ssr: CheckedBaggageSsrFlags,
  source: 'extra' | 'selected' = 'extra',
): boolean => {
  if (isPurchasedCheckedBaggage(ssr)) {
    return true;
  }
  if (source !== 'selected') {
    return false;
  }
  if (ssr.isIncluded === false) {
    return true;
  }
  return false;
};

const matchesPurchasedIncludedBaggage = (ssr: CheckedBaggageSsrFlags, pax: Pax): boolean =>
  pax.includedExtras.some(
    (extra) =>
      isCheckedBaggageSsrType(extra.ssrType) &&
      extra.codeType !== null &&
      extra.codeType !== undefined &&
      extra.codeType === ssr.codeType &&
      isPurchasedCheckedBaggage(extra),
  );

const isSelectedAdditionalCheckedBaggage = (ssr: IncludedExtra, pax: Pax): boolean =>
  isAdditionalCheckedBaggage(ssr, 'selected') || matchesPurchasedIncludedBaggage(ssr, pax);

/**
 * Checked baggage from includedExtras.
 * `isAdditional === true` → isPurchased; `false` → included base bag.
 * Hand baggage stays on HANDBAGGAGE via getExtraByType — not mixed here.
 */
const getCheckedBaggageExtra = (
  pax: Pax,
  isAdditional: boolean,
): Pax['includedExtras'][number] | undefined =>
  pax.includedExtras.find(
    (extra) =>
      isCheckedBaggageSsrType(extra.ssrType) && isAdditionalCheckedBaggage(extra) === isAdditional,
  );

const getAssistExtras = (pax: Pax): Pax['includedExtras'] =>
  pax.includedExtras.filter(
    (extra) => extra.ssrType.toLocaleLowerCase() === SsrType.ASSIST.toLocaleLowerCase(),
  );

const getAssistSlotPair = (
  assist?: Pick<IncludedExtra, 'assistSlotFrom' | 'assistSlotTo'> | null,
): { slotFrom: string; slotTo: string } | undefined => {
  const slotFrom = assist?.assistSlotFrom?.trim();
  const slotTo = assist?.assistSlotTo?.trim();

  if (!slotFrom || !slotTo) {
    return undefined;
  }

  return { slotFrom, slotTo };
};

const hasAssistSlots = (
  assist?: Pick<IncludedExtra, 'assistSlotFrom' | 'assistSlotTo'> | null,
): boolean => Boolean(getAssistSlotPair(assist));

const getPreferredAssistExtra = (
  assistExtras: Pax['includedExtras'],
): Pax['includedExtras'][number] | undefined =>
  assistExtras.find((assist) => hasAssistSlots(assist)) ?? assistExtras[0];

const mapExtraToSsr = (
  ssrType: SsrType,
  extra: Pax['includedExtras'][number] | undefined,
  codeTypeList: CodeTypeItem[],
  mealsListData?: MealsList,
): string | undefined => {
  if (!extra) {
    return undefined;
  }

  return getSsrValue(ssrType, extra, codeTypeList, mealsListData);
};

// Map passenger type number to string
const mapPassengerTypeToString = (type: number): PassengerTypeEnum => {
  const typeMap: Record<number, PassengerTypeEnum> = {
    1: PassengerTypeEnum.Adult,
    6: PassengerTypeEnum.Child,
    5: PassengerTypeEnum.Infant,
  };
  return typeMap[type] ?? PassengerTypeEnum.Adult;
};

const resolveAssociatedInfantId = (
  infantResPaxId?: number | null,
  travelsWithPassengerId?: number | null,
): string => {
  if (infantResPaxId) {
    return String(infantResPaxId);
  }
  if (travelsWithPassengerId) {
    return String(travelsWithPassengerId);
  }
  return '';
};

/**
 * Find SSR from selectedSSRList for a specific passenger and leg.
 * Accepts both camelCase and PascalCase flight-id fields returned by the API.
 */
const isMatchingSsrTypeAndPassenger = (
  ssr: IncludedExtra,
  ssrType: SsrType,
  resPaxId: number,
): boolean => {
  const ssrWithDetails = ssr as IncludedExtra & {
    resPaxId?: number;
    resPaxID?: number;
  };

  if (ssr.ssrType.toLocaleLowerCase() !== ssrType.toLocaleLowerCase()) {
    return false;
  }

  return Number(ssrWithDetails.resPaxId ?? ssrWithDetails.resPaxID) === resPaxId;
};

const getSsrFlightIds = (
  ssr: IncludedExtra,
): {
  physicalFlightId: string;
  logicalFlightId: string;
} => {
  const ssrWithDetails = ssr as IncludedExtra & {
    physicalFlightID?: string;
    physicalFlightId?: string;
    logicalFlightID?: string;
    logicalFlightId?: string;
  };

  return {
    physicalFlightId: String(
      ssrWithDetails.physicalFlightID ?? ssrWithDetails.physicalFlightId ?? '',
    ),
    logicalFlightId: String(ssrWithDetails.logicalFlightID ?? ssrWithDetails.logicalFlightId ?? ''),
  };
};

const isMatchingAssistFlight = (
  ssr: IncludedExtra,
  physicalFlightID: string,
  logicalFlightID?: string,
): boolean => {
  const { physicalFlightId, logicalFlightId } = getSsrFlightIds(ssr);

  if (physicalFlightId && physicalFlightId === String(physicalFlightID)) {
    return true;
  }

  if (!physicalFlightId && !logicalFlightId) {
    return true;
  }

  return Boolean(logicalFlightID && logicalFlightId && logicalFlightId === String(logicalFlightID));
};

const getAssistCandidates = (
  selectedSSRList: IncludedExtra[],
  matchingSsrs: IncludedExtra[],
): IncludedExtra[] => {
  if (matchingSsrs.length > 0) {
    return matchingSsrs;
  }

  // Some ASSIST payloads do not include passenger ids; fallback to all ASSIST SSRs.
  return selectedSSRList.filter(
    (ssr) => ssr.ssrType.toLocaleLowerCase() === SsrType.ASSIST.toLocaleLowerCase(),
  );
};

const findSelectedSsr = (
  selectedSSRList: IncludedExtra[],
  resPaxId: number,
  physicalFlightID: string,
  ssrType: SsrType,
  logicalFlightID?: string,
): IncludedExtra | undefined => {
  const matchingSsrs = selectedSSRList.filter((ssr) =>
    isMatchingSsrTypeAndPassenger(ssr, ssrType, resPaxId),
  );

  const isBaggage = ssrType.toLocaleLowerCase() === SsrType.CHECKEDBAGGAGE.toLocaleLowerCase();
  const isAssist = ssrType.toLocaleLowerCase() === SsrType.ASSIST.toLocaleLowerCase();

  if (isBaggage) {
    return matchingSsrs.find((ssr) => {
      if (!logicalFlightID) {
        return false;
      }

      const { logicalFlightId } = getSsrFlightIds(ssr);
      return logicalFlightId === String(logicalFlightID);
    });
  }

  if (isAssist) {
    const assistCandidates = getAssistCandidates(selectedSSRList, matchingSsrs);

    const matchingAssistSsrs = assistCandidates.filter((ssr) =>
      isMatchingAssistFlight(ssr, physicalFlightID, logicalFlightID),
    );

    const preferredAssistWithSlot = matchingAssistSsrs.find((ssr) => hasAssistSlots(ssr));

    return preferredAssistWithSlot ?? matchingAssistSsrs[0];
  }

  return matchingSsrs.find((ssr) => {
    const { physicalFlightId } = getSsrFlightIds(ssr);

    // Other SSRs (SEAT, LOUNGE, etc.) — match by physicalFlightID
    return physicalFlightId === String(physicalFlightID);
  });
};

/**
 * Build SSR value from selectedSSRList item
 */
const buildSsrValueFromSelected = (
  selectedSsr: IncludedExtra,
  ssrType: SsrType,
  codeTypeList: CodeTypeItem[],
): string | undefined => {
  if (ssrType === SsrType.SEAT) {
    // For seat, combine row + column (e.g., row: 10, column: "C" = "10C")
    const ssrWithSeatInfo = selectedSsr as IncludedExtra & {
      row?: number;
      seat?: string;
      column?: string;
    };
    const row = ssrWithSeatInfo.row;
    const seat = ssrWithSeatInfo.seat ?? ssrWithSeatInfo.column;
    if (row !== undefined && seat) {
      return `${String(row)}${seat}`;
    }
  }

  // For other SSR types (baggage, meals, etc.), use the standard getSsrValue
  return getSsrValue(ssrType, selectedSsr, codeTypeList);
};

// Resolve airport city name from code using airports master data
const resolveAirportCity = (code: string, airports: Airport[]): string =>
  airports.find((a) => a.code === code)?.city ?? code;

// Helper to find all checked-in legs for a passenger
interface CheckedInLegData {
  leg: Leg;
  pax: Pax;
  logicalFlightID?: string;
  notConnection?: boolean;
}

interface PassengerLegData {
  leg: Leg;
  pax: Pax;
  logicalFlightID?: string;
  notConnection?: boolean;
}

const TWO_FLIGHTS_COUNT = 2;

const resolveNotConnection = (flight: BookingResponse['flights'][number]): boolean =>
  Boolean(Array.isArray(flight.stops) && flight.stops[0]?.notConnection);

const toPassengerLegData = (
  leg: Leg,
  pax: Pax,
  flight: BookingResponse['flights'][number],
): PassengerLegData => ({
  leg,
  pax,
  logicalFlightID: flight.logicalFlightID,
  notConnection: resolveNotConnection(flight),
});

/** Legs shown in checked-in UI: CheckedIn or any standby/waitlist status. */
const findCheckedInDisplayLegsForPassenger = (
  resPaxId: number,
  flights: BookingResponse['flights'],
): CheckedInLegData[] => {
  const result: CheckedInLegData[] = [];
  const isCheckedInAcrossTwoFlights = isPaxCheckedInAcrossTwoFlights(flights, resPaxId);

  for (const flight of flights) {
    let isLegInterline = false;
    for (const leg of flight.legs) {
      const paxEntry = leg.pax.find((p) => p.resPaxId === resPaxId);
      if (leg.isInterline) {
        isLegInterline = isPaxCheckedInOnFzLegIfInterline(flight, resPaxId);
      }
      if (
        paxEntry &&
        (isCheckedInStatus(paxEntry.status) ||
          isStandByStatus(paxEntry.status) ||
          isLegInterline ||
          isCheckedInAcrossTwoFlights)
      ) {
        result.push(toPassengerLegData(leg, paxEntry, flight));
      }
    }
  }

  return result;
};

/**
 * When partially checked in, show every leg (CI + non-CI) with per-leg status and SSR.
 * Otherwise keep the filtered checked-in / standby / interline display set.
 */
const resolveCheckedInDisplayLegsForPassenger = (
  resPaxId: number,
  flights: BookingResponse['flights'],
): CheckedInLegData[] => {
  const allLegs = findAllLegsForPassenger(resPaxId, flights);
  const checkedInCount = countCheckedInFlights(allLegs.map((entry) => entry.pax.status));

  if (checkedInCount > 0 && checkedInCount < allLegs.length) {
    return allLegs;
  }

  return findCheckedInDisplayLegsForPassenger(resPaxId, flights);
};

const findAllLegsForPassenger = (
  resPaxId: number,
  flights: BookingResponse['flights'],
): PassengerLegData[] => {
  const result: PassengerLegData[] = [];

  for (const flight of flights) {
    for (const leg of flight.legs) {
      const paxEntry = leg.pax.find((p) => p.resPaxId === resPaxId);
      if (paxEntry) {
        result.push(toPassengerLegData(leg, paxEntry, flight));
      }
    }
  }

  return result;
};

const buildPaxStatusCounters = (
  passengerLegs: PassengerLegData[],
): {
  statuses: (string | undefined)[];
  isAllowCheckInCount: number;
  checkedInFlightsCount: number;
  standByFlightsCount: number;
  standByID90: number;
  standByWaitlist: boolean;
  leftBorderColor: ReturnType<typeof resolveLeftBorderColor>;
  canSelect: boolean;
} => {
  const statuses = passengerLegs.map((entry) => entry.pax.status);
  const isAllowCheckInCount = countAllowCheckIn(
    passengerLegs.map((entry) => entry.pax.paxRestrictions?.allowedCheckin),
  );
  const checkedInFlightsCount = countCheckedInFlights(statuses);
  const standByFlightsCount = countStandByFlights(statuses);
  const standByID90 = resolveStandByID90(statuses);
  const standByWaitlist = resolveStandByWaitlist(statuses);
  const leftBorderColor = resolveLeftBorderColor({
    isAllowCheckInCount,
    legsLength: passengerLegs.length,
    checkedInFlightsCount,
    standByFlightsCount,
  });

  return {
    statuses,
    isAllowCheckInCount,
    checkedInFlightsCount,
    standByFlightsCount,
    standByID90,
    standByWaitlist,
    leftBorderColor,
    canSelect: canSelectForCheckIn(isAllowCheckInCount, passengerLegs.length),
  };
};

// Aggregate restrictions from all checked-in legs
const aggregateRestrictions = (
  legData: PassengerLegData[],
): Pick<
  CheckedInPax,
  | 'allowedEmail'
  | 'allowedSMS'
  | 'allowedWAPP'
  | 'allowedBoardingPass'
  | 'allowedOffload'
  | 'allowedCheckin'
  | 'bpMsgKey'
  | 'loungeEligibility'
> => {
  if (legData.length === 0) {
    return {
      allowedEmail: false,
      allowedSMS: false,
      allowedWAPP: false,
      allowedBoardingPass: false,
      allowedOffload: false,
      allowedCheckin: false,
      bpMsgKey: null,
      loungeEligibility: false,
    };
  }

  const firstLegPaxRestrictions = legData[0]?.pax.paxRestrictions;
  const bpMsgKey =
    legData.find((ld) => (ld.pax.paxRestrictions?.bpMsgKey ?? null) !== null)?.pax.paxRestrictions
      ?.bpMsgKey ?? null;

  return {
    allowedEmail: legData.every((ld) => ld.pax.paxRestrictions?.allowedEmail ?? false),
    allowedSMS: legData.every((ld) => ld.pax.paxRestrictions?.allowedSMS ?? false),
    allowedWAPP: legData.every((ld) => ld.pax.paxRestrictions?.allowedWAPP ?? false),
    allowedCheckin: legData.every((ld) => ld.pax.paxRestrictions?.allowedCheckin ?? false),
    // Legacy flow merges boarding-pass eligibility across legs.
    allowedBoardingPass: legData.some((ld) => ld.pax.paxRestrictions?.allowedBoardingPass ?? false),
    // Legacy flow uses first leg offload flag.
    allowedOffload: firstLegPaxRestrictions?.allowedOffload ?? false,
    bpMsgKey,
    loungeEligibility: legData.some((ld) => ld.pax.paxRestrictions?.loungeEligibility ?? false),
  };
};

const findSelectedCheckedBaggageByAmount = (
  selectedSSRList: IncludedExtra[],
  pax: Pax,
  logicalFlightID: string | undefined,
  isAdditional: boolean,
): IncludedExtra | undefined => {
  if (!logicalFlightID) {
    return undefined;
  }

  return selectedSSRList.find((ssr) => {
    if (!isMatchingSsrTypeAndPassenger(ssr, SsrType.CHECKEDBAGGAGE, pax.resPaxId)) {
      return false;
    }

    const { logicalFlightId } = getSsrFlightIds(ssr);
    if (logicalFlightId !== String(logicalFlightID)) {
      return false;
    }

    return isSelectedAdditionalCheckedBaggage(ssr, pax) === isAdditional;
  });
};

const findSelectedSsrsForLeg = (
  selectedSSRList: IncludedExtra[],
  pax: Pax,
  physicalFlightID: string,
  logicalFlightID?: string,
): {
  selectedSeat: IncludedExtra | undefined;
  selectedHandBaggage: IncludedExtra | undefined;
  selectedCheckedBaggage: IncludedExtra | undefined;
  selectedAdditionalCheckedBaggage: IncludedExtra | undefined;
  selectedMeal: IncludedExtra | undefined;
  selectedLounge: IncludedExtra | undefined;
  selectedAssist: IncludedExtra | undefined;
} => {
  const byType = (ssrType: SsrType): IncludedExtra | undefined =>
    findSelectedSsr(selectedSSRList, pax.resPaxId, physicalFlightID, ssrType, logicalFlightID);

  return {
    selectedSeat: byType(SsrType.SEAT),
    selectedHandBaggage: byType(SsrType.HANDBAGGAGE),
    selectedCheckedBaggage: findSelectedCheckedBaggageByAmount(
      selectedSSRList,
      pax,
      logicalFlightID,
      false,
    ),
    selectedAdditionalCheckedBaggage: findSelectedCheckedBaggageByAmount(
      selectedSSRList,
      pax,
      logicalFlightID,
      true,
    ),
    selectedMeal: byType(SsrType.MEAL),
    selectedLounge: byType(SsrType.LOUNGE),
    selectedAssist: byType(SsrType.ASSIST),
  };
};

const extractPaxExtras = (
  pax: Pax,
): {
  seatExtra: Pax['includedExtras'][number] | undefined;
  handBaggageExtra: Pax['includedExtras'][number] | undefined;
  checkedBaggageExtra: Pax['includedExtras'][number] | undefined;
  addCheckedBaggageExtra: Pax['includedExtras'][number] | undefined;
  mealExtra: Pax['includedExtras'][number] | undefined;
  ifeExtra: Pax['includedExtras'][number] | undefined;
  loungeExtra: Pax['includedExtras'][number] | undefined;
  assistExtra: Pax['includedExtras'][number] | undefined;
} => {
  const assistExtras = getAssistExtras(pax);

  return {
    seatExtra: getExtraByType(pax, SsrType.SEAT),
    handBaggageExtra: getExtraByType(pax, SsrType.HANDBAGGAGE),
    checkedBaggageExtra: getCheckedBaggageExtra(pax, false),
    addCheckedBaggageExtra: getCheckedBaggageExtra(pax, true),
    mealExtra: getExtraByType(pax, SsrType.MEAL),
    ifeExtra: getExtraByType(pax, SsrType.IFE),
    loungeExtra: getExtraByType(pax, SsrType.LOUNGE),
    assistExtra: getPreferredAssistExtra(assistExtras),
  };
};

const resolveAssistLegState = (
  selectedAssist: IncludedExtra | undefined,
  assistExtra: Pax['includedExtras'][number] | undefined,
): {
  hasAssist: boolean;
  isAssistOpted?: boolean;
  assistSSR?: string;
} => {
  const assistSource = selectedAssist ?? assistExtra;
  if (!assistSource) {
    return { hasAssist: false };
  }

  // Prefer explicit opt-out from either source so confirmation/landing can hide MASD.
  if (selectedAssist?.isAssistOpted === false || assistExtra?.isAssistOpted === false) {
    return { hasAssist: true, isAssistOpted: false };
  }

  return {
    hasAssist: true,
    isAssistOpted: true,
    assistSSR: resolveAssistDisplayValue(selectedAssist, assistExtra),
  };
};

const resolveAssistDisplayValue = (
  selectedAssist: IncludedExtra | undefined,
  assistExtra: Pax['includedExtras'][number] | undefined,
): string => {
  const slotValue = resolveAssistSlotValue(selectedAssist, assistExtra);
  if (slotValue?.trim()) {
    return slotValue;
  }

  // Opted-in without a confirmed timeslot (or "arrival time not confirmed") → Included.
  return ASSIST_INCLUDED_TEXT_KEY;
};

const resolveAssistSlotValue = (
  selectedAssist: IncludedExtra | undefined,
  assistExtra: Pax['includedExtras'][number] | undefined,
): string | undefined => {
  // Prefer a complete slot pair from either source; treat "" as missing so a
  // non-slotted selectedSSRList ASSIST cannot block includedExtras timings.
  const slots = getAssistSlotPair(selectedAssist) ?? getAssistSlotPair(assistExtra);

  return slots ? formatAssistTimeSlot(slots.slotFrom, slots.slotTo) : undefined;
};

const resolveSelectedOrExtraSsr = (
  selectedSsr: IncludedExtra | undefined,
  extra: Pax['includedExtras'][number] | undefined,
  ssrType: SsrType,
  codeTypeList: CodeTypeItem[],
): string | undefined =>
  selectedSsr
    ? buildSsrValueFromSelected(selectedSsr, ssrType, codeTypeList)
    : mapExtraToSsr(ssrType, extra, codeTypeList);

const resolveCheckedBaggageSsr = (
  selectedCheckedBaggage: IncludedExtra | undefined,
  checkedBaggageExtra: Pax['includedExtras'][number] | undefined,
  codeTypeList: CodeTypeItem[],
): string | undefined =>
  selectedCheckedBaggage
    ? buildSsrValueFromSelected(selectedCheckedBaggage, SsrType.CHECKEDBAGGAGE, codeTypeList)
    : mapExtraToSsr(SsrType.CHECKEDBAGGAGE, checkedBaggageExtra, codeTypeList);

const resolveAdditionalCheckedBaggageSsr = (
  selectedAdditionalCheckedBaggage: IncludedExtra | undefined,
  addCheckedBaggageExtra: Pax['includedExtras'][number] | undefined,
  codeTypeList: CodeTypeItem[],
): string =>
  selectedAdditionalCheckedBaggage
    ? (buildSsrValueFromSelected(
        selectedAdditionalCheckedBaggage,
        SsrType.CHECKEDBAGGAGE,
        codeTypeList,
      ) ?? '')
    : (mapExtraToSsr(SsrType.CHECKEDBAGGAGE, addCheckedBaggageExtra, codeTypeList) ?? '');

const resolveLoungeSsr = (
  selectedLounge: IncludedExtra | undefined,
  loungeExtra: Pax['includedExtras'][number] | undefined,
  codeTypeList: CodeTypeItem[],
): string | undefined => {
  if (!(selectedLounge ?? loungeExtra)) {
    return undefined;
  }

  const loungeCodeTypeRaw = selectedLounge?.codeType ?? loungeExtra?.codeType ?? null;
  return resolveCodeName(loungeCodeTypeRaw, codeTypeList) ?? loungeCodeTypeRaw ?? undefined;
};

/** Meals are leg-level — prefer selectedSSRList match by physicalFlightID, then includedExtras. */
const resolveMealSsr = (
  selectedMeal: IncludedExtra | undefined,
  mealExtra: Pax['includedExtras'][number] | undefined,
  codeTypeList: CodeTypeItem[],
  mealsListData?: MealsList,
): string | undefined => {
  if (!(selectedMeal ?? mealExtra)) {
    return undefined;
  }

  const mealCodeTypeRaw = selectedMeal?.codeType ?? mealExtra?.codeType ?? null;
  return (
    getMealName(mealCodeTypeRaw, mealsListData ?? [], codeTypeList) ?? mealCodeTypeRaw ?? undefined
  );
};

const buildBaseSsrValues = (
  selectedSsrs: ReturnType<typeof findSelectedSsrsForLeg>,
  paxExtras: ReturnType<typeof extractPaxExtras>,
  codeTypeList: CodeTypeItem[],
  mealsListData?: MealsList,
): {
  seatSSR: string | undefined;
  handBaggageSSR: string | undefined;
  checkedBaggageSSR: string | undefined;
  additionCheckedBaggage: string;
  mealSSR: string | undefined;
  ifeSSR: string | undefined;
  loungeSSR: string | undefined;
} => {
  const {
    selectedSeat,
    selectedHandBaggage,
    selectedCheckedBaggage,
    selectedAdditionalCheckedBaggage,
    selectedMeal,
    selectedLounge,
  } = selectedSsrs;
  const {
    seatExtra,
    handBaggageExtra,
    checkedBaggageExtra,
    addCheckedBaggageExtra,
    mealExtra,
    ifeExtra,
    loungeExtra,
  } = paxExtras;

  return {
    seatSSR: resolveSelectedOrExtraSsr(selectedSeat, seatExtra, SsrType.SEAT, codeTypeList),
    handBaggageSSR: resolveSelectedOrExtraSsr(
      selectedHandBaggage,
      handBaggageExtra,
      SsrType.HANDBAGGAGE,
      codeTypeList,
    ),
    checkedBaggageSSR: resolveCheckedBaggageSsr(
      selectedCheckedBaggage,
      checkedBaggageExtra,
      codeTypeList,
    ),
    additionCheckedBaggage: resolveAdditionalCheckedBaggageSsr(
      selectedAdditionalCheckedBaggage,
      addCheckedBaggageExtra,
      codeTypeList,
    ),
    mealSSR: resolveMealSsr(selectedMeal, mealExtra, codeTypeList, mealsListData),
    ifeSSR: mapExtraToSsr(SsrType.IFE, ifeExtra, codeTypeList),
    loungeSSR: resolveLoungeSsr(selectedLounge, loungeExtra, codeTypeList),
  };
};

const buildSsrValues = (
  selectedSsrs: ReturnType<typeof findSelectedSsrsForLeg>,
  paxExtras: ReturnType<typeof extractPaxExtras>,
  codeTypeList: CodeTypeItem[],
  mealsListData?: MealsList,
): {
  seatSSR: string | undefined;
  handBaggageSSR: string | undefined;
  checkedBaggageSSR: string | undefined;
  additionCheckedBaggage: string;
  mealSSR: string | undefined;
  ifeSSR: string | undefined;
  loungeSSR: string | undefined;
  hasAssist: boolean;
  isAssistOpted?: boolean;
  assistSSR?: string;
} => {
  const { selectedAssist } = selectedSsrs;
  const { assistExtra } = paxExtras;
  const baseSsrValues = buildBaseSsrValues(selectedSsrs, paxExtras, codeTypeList, mealsListData);

  return {
    ...baseSsrValues,
    ...resolveAssistLegState(selectedAssist, assistExtra),
  };
};

const mapLegToCheckedInPaxLeg = (
  leg: Leg,
  pax: Pax,
  airports: Airport[],
  codeTypeList: CodeTypeItem[],
  selectedSSRList: IncludedExtra[],
  mealsListData?: MealsList,
  logicalFlightID?: string,
  notConnection?: boolean,
): CheckedInPaxLeg => {
  const flightClass = getClassValue(leg);

  const selectedSsrs = findSelectedSsrsForLeg(
    selectedSSRList,
    pax,
    leg.physicalFlightID,
    logicalFlightID,
  );

  const paxExtras = extractPaxExtras(pax);

  const ssrValues = buildSsrValues(selectedSsrs, paxExtras, codeTypeList, mealsListData);
  const seatExtra = paxExtras.seatExtra ?? selectedSsrs.selectedSeat;

  return {
    origin: leg.originCity || resolveAirportCity(leg.origin, airports),
    destination: leg.destinationCity || resolveAirportCity(leg.destination, airports),
    originCode: leg.origin,
    destinationCode: leg.destination,
    flightNumber: `${leg.sellingCarrier} ${leg.flightNum}`.trim(),
    flightClass,
    departureDate: leg.departureDate,
    arrivalDate: leg.arrivalDate,
    ...ssrValues,
    physicalFlightID: leg.physicalFlightID,
    logicalFlightID,
    notConnection,
    resPaxId: pax.resPaxId,
    paxJourneyId: pax.paxJourneyId,
    allowedBoardingPass: pax.paxRestrictions?.allowedBoardingPass ?? false,
    status: pax.status,
    paxRestrictions: pax.paxRestrictions,
    bpMsgKey: pax.paxRestrictions?.bpMsgKey ?? null,
    isInterline: leg.isInterline ?? false,
    operatingCarrier: leg.operatingCarrier,
    isIFEBusinessRestricted: leg.isIFEBusinessRestricted,
    hasSeatExtra: Boolean(seatExtra),
    seatAssignmentType: seatExtra?.seatAssignmentType,
    seatInclusiveFare: leg.seatInclusiveFare,
    isAlreadyPaidSeat: seatExtra?.amount,
  };
};

/**
 * Checks if the given leg is interline, then finds the FZ leg in the same flight and checks if the passenger is checked in on that leg.
 * @param flight The flight object containing legs
 * @param paxId The passenger's resPaxId
 * @returns true if FZ leg exists and passenger is checked in on it, false otherwise
 */
export function isPaxCheckedInOnFzLegIfInterline(flight: { legs: Leg[] }, paxId: number): boolean {
  // Find the FZ leg
  const fzLeg = flight.legs.find((leg) => leg.operatingCarrier === 'FZ');
  if (!fzLeg) return false;
  // Find the pax entry in the FZ leg
  const paxEntry = fzLeg.pax.find((p) => p.resPaxId === paxId);
  if (!paxEntry) return false;
  // Check if status is CheckedIn
  return String(paxEntry.status) === String(StatusType.CHECKED_IN);
}

/**
 * For same-day return (two flights), if a passenger is checked in on any leg,
 * treat them as checked in for both flights.
 */
export function isPaxCheckedInAcrossTwoFlights(
  flights: BookingResponse['flights'],
  paxId: number,
): boolean {
  if (flights.length !== TWO_FLIGHTS_COUNT) {
    return false;
  }

  return flights.some((flight) =>
    flight.legs.some((leg) => {
      const paxEntry = leg.pax.find((p) => p.resPaxId === paxId);
      if (!paxEntry) {
        return false;
      }

      if (String(paxEntry.status) === String(StatusType.CHECKED_IN)) {
        return true;
      }

      return Boolean(leg.isInterline) && isPaxCheckedInOnFzLegIfInterline(flight, paxId);
    }),
  );
}

const toCheckedInPassenger = (
  passenger: BookingResponse['paxList'][number],
  passengerLegs: PassengerLegData[],
  counters: ReturnType<typeof buildPaxStatusCounters>,
  legs: CheckedInPaxLeg[],
  infantResPaxId?: number | null,
): CheckedInPax => ({
  paxId: String(passenger.resPaxId),
  resPaxId: passenger.resPaxId,
  isPrimaryPassenger: passenger.isPrimaryPassenger,
  travelsWithPassengerId: passenger.travelsWithPassengerId,
  title: passenger.title,
  firstName: passenger.firstName,
  lastName: passenger.lastName,
  fullName: formatPassengerName(passenger.title, passenger.firstName, passenger.lastName),
  passengerType: mapPassengerTypeToString(passenger.passengerType),
  associatedInfantID: resolveAssociatedInfantId(infantResPaxId, passenger.travelsWithPassengerId),
  isAllowCheckInCount: counters.isAllowCheckInCount,
  checkedInFlightsCount: counters.checkedInFlightsCount,
  totalLegsCount: passengerLegs.length,
  standByFlightsCount: counters.standByFlightsCount,
  standByID90: counters.standByID90,
  standByWaitlist: counters.standByWaitlist,
  leftBorderColor: counters.leftBorderColor,
  canSelectForCheckIn: counters.canSelect,
  legs,
  ...aggregateRestrictions(passengerLegs),
});

/**
 * Maps BookingResponse to CheckedInPax array
 * Works directly with response data, does not call mapBookingResponseToPassengers
 */
export const mapBookingResponseToCheckedInPassengers = (
  response: BookingResponse,
  codeTypeList: CodeTypeItem[],
  airports: Airport[],
  mealsList: MealsList,
): CheckedInPax[] => {
  const checkedInPassengers: CheckedInPax[] = [];
  for (const passenger of response.paxList) {
    const passengerLegs = findAllLegsForPassenger(passenger.resPaxId, response.flights);
    const counters = buildPaxStatusCounters(passengerLegs);

    if (counters.checkedInFlightsCount === 0 && counters.standByFlightsCount === 0) {
      continue;
    }

    const displayLegs = resolveCheckedInDisplayLegsForPassenger(
      passenger.resPaxId,
      response.flights,
    );
    if (displayLegs.length === 0) {
      continue;
    }

    checkedInPassengers.push(
      toCheckedInPassenger(
        passenger,
        passengerLegs,
        counters,
        displayLegs.map(({ leg, pax, logicalFlightID, notConnection }) =>
          mapLegToCheckedInPaxLeg(
            leg,
            pax,
            airports,
            codeTypeList,
            response.selectedSSRList,
            mealsList,
            logicalFlightID,
            notConnection,
          ),
        ),
        getInfantResPaxIdByParent(response.paxList, passenger.resPaxId),
      ),
    );
  }

  return checkedInPassengers;
};

// =======================
// 🔹 Not-Checked-In Passengers Mapper
// =======================

/**
 * Maps a not-checked-in leg directly with its SSR values
 */
// eslint-disable-next-line max-lines-per-function, complexity
const mapNotCheckedInPaxFlightLeg = (
  leg: Leg,
  pax: Pax,
  codeTypeList: CodeTypeItem[],
  selectedSSRList: IncludedExtra[],
  logicalFlightID?: string,
  mealsList?: MealsList,
): {
  origin: string;
  destination: string;
  physicalFlightID: string;
  flightClass: string;
  seatSSR?: string;
  seatPrice?: number;
  seatDeltaPrice?: number;
  isSeatFromIncludedExtras?: boolean;
  isSeatPurchased?: boolean;
  handBaggageSSR?: string;
  checkedBaggageSSR?: string;
  checkedBaggageCodeType?: string;
  checkedBaggagePrice?: number;
  isBaggagePurchased?: boolean;
  isInterline: boolean;
  additionalCheckedBaggageSSR: string;
  additionalCheckedBaggageCodeType?: string;
  additionalCheckedBaggagePrice?: number;
  mealSSR?: string;
  mealCodeType?: string;
  mealPrice?: number;
  loungeSSR?: string;
  loungeCodeType?: string;
  loungePrice?: number;
  isLoungeFromIncludedExtras?: boolean;
  isLoungeEligible?: boolean;
  assistSSR?: string;
  assistCodeType?: string;
  assistSubCodeType?: string;
  assistPrice?: number;
  flightNum?: string;
  operatingCarrier?: string;
} => {
  type SelectedBaggageSsr = IncludedExtra & {
    resPaxId?: number;
    logicalFlightID?: string;
    logicalFlightId?: string;
    isIncluded?: boolean;
  };

  const isSelectedBaggageForPassengerFlight = (ssr: IncludedExtra): boolean => {
    const ssrWithDetails = ssr as SelectedBaggageSsr;

    return (
      isCheckedBaggageSsrType(ssr.ssrType) &&
      ssrWithDetails.resPaxId === pax.resPaxId &&
      (ssrWithDetails.logicalFlightID === logicalFlightID ||
        ssrWithDetails.logicalFlightId === logicalFlightID)
    );
  };

  /** Additional: isPurchased, or paid selectedSSRList items that omit isPurchased. */
  const isSelectedAdditionalBaggage = (ssr: IncludedExtra): boolean =>
    isSelectedAdditionalCheckedBaggage(ssr, pax);

  const flightClass = getClassValue(leg);

  // Try to find SSR from selectedSSRList first (priority), then fallback to includedExtras
  const selectedSeat = findSelectedSsr(
    selectedSSRList,
    pax.resPaxId,
    leg.physicalFlightID,
    SsrType.SEAT,
    logicalFlightID,
  );
  const selectedHandBaggage = findSelectedSsr(
    selectedSSRList,
    pax.resPaxId,
    leg.physicalFlightID,
    SsrType.HANDBAGGAGE,
    logicalFlightID,
  );

  // Split base (not purchased) vs additional (isPurchased) — same ssrType BAGGAGE
  const selectedIncludedBaggage = selectedSSRList.find(
    (ssr) => isSelectedBaggageForPassengerFlight(ssr) && !isSelectedAdditionalBaggage(ssr),
  );

  const selectedPurchasedBaggage = selectedSSRList.find(
    (ssr) => isSelectedBaggageForPassengerFlight(ssr) && isSelectedAdditionalBaggage(ssr),
  );

  // Extract SSR extras by type (fallback)
  const seatExtra = pax.includedExtras.find(
    (e) => e.ssrType.toLocaleLowerCase() === SsrType.SEAT.toLocaleLowerCase(),
  );
  const handBaggageExtra = pax.includedExtras.find(
    (e) => e.ssrType.toLocaleLowerCase() === SsrType.HANDBAGGAGE.toLocaleLowerCase(),
  );
  const checkedBaggageExtra = getCheckedBaggageExtra(pax, false);
  const addCheckedBaggage = getCheckedBaggageExtra(pax, true);

  // Find meal SSR from selectedSSRList (meals are leg-level, match by physicalFlightID)
  const selectedMeal = findSelectedSsr(
    selectedSSRList,
    pax.resPaxId,
    leg.physicalFlightID,
    SsrType.MEAL,
    logicalFlightID,
  );

  const selectedLounge = findSelectedSsr(
    selectedSSRList,
    pax.resPaxId,
    leg.physicalFlightID,
    SsrType.LOUNGE,
    logicalFlightID,
  );

  const selectedAssist = findSelectedSsr(
    selectedSSRList,
    pax.resPaxId,
    leg.physicalFlightID,
    SsrType.ASSIST,
    logicalFlightID,
  );

  // Fallback to included extras
  const mealExtra = pax.includedExtras.find(
    (e) => e.ssrType.toLocaleLowerCase() === SsrType.MEAL.toLocaleLowerCase(),
  );

  const loungeExtra = pax.includedExtras.find(
    (e) => e.ssrType.toLocaleLowerCase() === SsrType.LOUNGE.toLocaleLowerCase(),
  );

  const assistExtra = getPreferredAssistExtra(getAssistExtras(pax));

  // Determine SSR values - prioritize selectedSSRList over includedExtras
  let seatSSR: string | undefined;
  if (selectedSeat) {
    seatSSR = buildSsrValueFromSelected(selectedSeat, SsrType.SEAT, codeTypeList);
  } else if (seatExtra) {
    seatSSR = getSsrValue(SsrType.SEAT, seatExtra, codeTypeList);
  }

  // Extract price for seat - use deltaAmount if it exists (not null/undefined), otherwise use amount
  const seatPrice =
    selectedSeat?.deltaAmount !== null && selectedSeat?.deltaAmount !== undefined
      ? selectedSeat.deltaAmount
      : (selectedSeat?.amount ?? seatExtra?.deltaAmount ?? seatExtra?.amount ?? 0);

  // Delta price for total calculation - use deltaAmount if it exists (even if 0), otherwise use seatPrice
  const seatDeltaPrice =
    selectedSeat?.deltaAmount !== null && selectedSeat?.deltaAmount !== undefined
      ? selectedSeat.deltaAmount
      : (seatExtra?.deltaAmount ?? seatPrice);

  // Determine if seat is from includedExtras or selectedSSRList
  // Priority: selectedSSRList takes precedence over includedExtras
  const isSeatFromIncludedExtras = Boolean(seatExtra) && !selectedSeat;
  const isSeatPurchased = selectedSeat?.isPurchased ?? seatExtra?.isPurchased ?? false;

  let handBaggageSSR: string | undefined;
  if (selectedHandBaggage) {
    handBaggageSSR = buildSsrValueFromSelected(
      selectedHandBaggage,
      SsrType.HANDBAGGAGE,
      codeTypeList,
    );
  } else if (handBaggageExtra) {
    handBaggageSSR = getSsrValue(SsrType.HANDBAGGAGE, handBaggageExtra, codeTypeList);
  }

  const hasBaseIncludedCheckedBaggage = Boolean(selectedIncludedBaggage ?? checkedBaggageExtra);
  const hasPaidCheckedBaggage = Boolean(selectedPurchasedBaggage ?? addCheckedBaggage);

  let checkedBaggageSSR: string | undefined;
  if (selectedIncludedBaggage) {
    checkedBaggageSSR = buildSsrValueFromSelected(
      selectedIncludedBaggage,
      SsrType.CHECKEDBAGGAGE,
      codeTypeList,
    );
  } else if (checkedBaggageExtra) {
    checkedBaggageSSR = getSsrValue(SsrType.CHECKEDBAGGAGE, checkedBaggageExtra, codeTypeList);
  }

  let additionalCheckedBaggageSSR = '';
  if (selectedPurchasedBaggage) {
    additionalCheckedBaggageSSR =
      buildSsrValueFromSelected(selectedPurchasedBaggage, SsrType.CHECKEDBAGGAGE, codeTypeList) ??
      '';
  } else if (addCheckedBaggage) {
    additionalCheckedBaggageSSR =
      getSsrValue(SsrType.CHECKEDBAGGAGE, addCheckedBaggage, codeTypeList) ?? '';
  }

  const checkedBaggagePrice = selectedIncludedBaggage?.amount ?? checkedBaggageExtra?.amount ?? 0;
  const checkedBaggageCodeType =
    selectedIncludedBaggage?.codeType ?? checkedBaggageExtra?.codeType ?? undefined;
  const additionalCheckedBaggagePrice =
    selectedPurchasedBaggage?.amount ?? addCheckedBaggage?.amount ?? 0;
  const additionalCheckedBaggageCodeType =
    selectedPurchasedBaggage?.codeType ?? addCheckedBaggage?.codeType ?? undefined;

  const isBaggagePurchased = hasPaidCheckedBaggage && !hasBaseIncludedCheckedBaggage;

  // Determine meal SSR value - prioritize selectedSSRList over includedExtras
  let mealSSR: string | undefined;
  const mealCodeTypeRaw = selectedMeal?.codeType ?? mealExtra?.codeType ?? null;
  const mealCodeType = mealCodeTypeRaw ?? undefined;

  if (selectedMeal ?? mealExtra) {
    // Try to get meal name using mealsList, then codeTypeList, finally use code directly
    mealSSR =
      getMealName(mealCodeTypeRaw, mealsList ?? [], codeTypeList) ?? mealCodeType ?? undefined;
  }

  // Extract price and codeType for meal
  const mealPrice = selectedMeal?.amount ?? mealExtra?.amount ?? 0;

  // Determine lounge SSR value - prioritize selectedSSRList over includedExtras
  let loungeSSR: string | undefined;
  const loungeCodeTypeRaw = selectedLounge?.codeType ?? loungeExtra?.codeType ?? null;
  const loungeCodeType = loungeCodeTypeRaw ?? undefined;
  const isLoungeFromIncludedExtras = Boolean(loungeExtra);

  if (selectedLounge ?? loungeExtra) {
    loungeSSR = resolveCodeName(loungeCodeTypeRaw, codeTypeList) ?? loungeCodeType ?? undefined;
  }

  // Determine lounge price: If lounge exists in includedExtras, it's always included (free)
  // This takes precedence over selectedSSRList
  let loungePrice = 0;

  if (loungeExtra) {
    // Lounge is in includedExtras - always treated as included (free), regardless of amount or isPurchased
    loungePrice = 0;
  } else if (selectedLounge) {
    // Lounge is only in selectedSSRList (not in includedExtras) - extract price from totalAmount or amount
    const selectedLoungeWithTotalAmount = selectedLounge as unknown as IncludedExtra & {
      totalAmount?: string | number;
    };
    const totalAmount = selectedLoungeWithTotalAmount.totalAmount;

    if (typeof totalAmount === 'number' && Number.isFinite(totalAmount)) {
      loungePrice = totalAmount;
    } else if (typeof totalAmount === 'string' && totalAmount.trim() !== '') {
      const parsedAmount = Number(totalAmount);
      if (Number.isFinite(parsedAmount)) {
        loungePrice = parsedAmount;
      } else {
        loungePrice = selectedLounge.amount;
      }
    } else {
      loungePrice = selectedLounge.amount;
    }
  }

  // Determine assist SSR value - prioritize selectedSSRList over includedExtras
  let assistSSR: string | undefined;
  const assistCodeTypeRaw = selectedAssist?.codeType ?? assistExtra?.codeType ?? null;
  const assistCodeType = assistCodeTypeRaw ?? undefined;
  const assistSubCodeTypeRaw = selectedAssist?.subCodeType ?? assistExtra?.subCodeType ?? null;
  const assistSubCodeType = assistSubCodeTypeRaw ?? undefined;

  if (selectedAssist ?? assistExtra) {
    const slotValue = resolveAssistSlotValue(selectedAssist, assistExtra);

    if (slotValue) {
      assistSSR = slotValue;
    } else {
      const assistCodeName = resolveCodeName(assistCodeTypeRaw, codeTypeList);
      assistSSR = assistCodeName ?? assistCodeType ?? undefined;
    }
  }

  // Extract price for assist
  const assistPrice = selectedAssist?.amount ?? assistExtra?.amount ?? 0;
  return {
    origin: leg.origin,
    destination: leg.destination,
    physicalFlightID: leg.physicalFlightID,
    flightNum: leg.flightNum,
    operatingCarrier: leg.operatingCarrier,
    flightClass,
    seatSSR,
    seatPrice,
    seatDeltaPrice,
    isSeatFromIncludedExtras,
    isSeatPurchased,
    handBaggageSSR,
    checkedBaggageSSR,
    checkedBaggageCodeType,
    checkedBaggagePrice,
    isBaggagePurchased,
    additionalCheckedBaggageSSR,
    additionalCheckedBaggageCodeType,
    additionalCheckedBaggagePrice,
    mealSSR,
    mealCodeType,
    mealPrice,
    loungeSSR,
    loungeCodeType,
    loungePrice,
    isLoungeFromIncludedExtras,
    isLoungeEligible:
      (pax.paxRestrictions?.allowedLounge ?? false) ||
      (pax.paxRestrictions?.loungeEligibility ?? false) ||
      Boolean(loungeExtra),
    assistSSR,
    assistCodeType,
    assistSubCodeType,
    assistPrice,
    isInterline: leg.isInterline,
  };
};

/**
 * Returns the resPaxId of the infant whose parentID matches the given resPaxId.
 * @param paxList Array of Passenger objects
 * @param parentResPaxId The resPaxId of the parent passenger
 * @returns The resPaxId of the matching infant, or undefined if not found
 */
export function getInfantResPaxIdByParent(
  paxList: {
    parentID: number | null;
    resPaxId: number;
    passengerType: number;
    travelsWithPassengerId: number | null;
  }[],
  parentResPaxId: number,
): number | undefined {
  // Convention: passengerType === 5 means Infant (see PassengerTypeEnum)
  // eslint-disable-next-line no-magic-numbers
  return paxList.find((p) => p?.travelsWithPassengerId === parentResPaxId && p.passengerType === 5)
    ?.resPaxId;
}

/**
 * Maps BookingResponse to NotCheckedInPax array
 * Direct mapping - find flights where passenger has not-checked-in legs
 *
 * eslint-disable-next-line complexity
 */

// eslint-disable-next-line max-lines-per-function, complexity
export function mapBookingResponseToNotCheckedInPassengers(
  response: BookingResponse,
  codeTypeList: CodeTypeItem[],
  mealsList?: MealsList,
): NotCheckedInPax[] {
  const notCheckedInPassengers: NotCheckedInPax[] = [];

  for (const passenger of response.paxList) {
    if (isPaxCheckedInAcrossTwoFlights(response.flights, passenger.resPaxId)) {
      continue;
    }

    const passengerLegs = findAllLegsForPassenger(passenger.resPaxId, response.flights);
    const counters = buildPaxStatusCounters(passengerLegs);

    // Selectable / open check-in list only — CheckedIn or standby belong in checkedInList.
    if (counters.checkedInFlightsCount > 0 || counters.standByFlightsCount > 0) {
      continue;
    }

    const flights: NotCheckedInPaxFlight[] = [];

    for (const flight of response.flights) {
      const notCheckedInLegs: NotCheckedInPaxFlightLeg[] = [];
      for (const leg of flight.legs) {
        let getFzLeg = false;
        const paxEntry = leg.pax.find((p) => p.resPaxId === passenger.resPaxId);
        if (leg.isInterline) {
          getFzLeg = isPaxCheckedInOnFzLegIfInterline(flight, passenger.resPaxId);
        }
        if (!getFzLeg && paxEntry && !isCheckedInStatus(paxEntry.status)) {
          notCheckedInLegs.push(
            mapNotCheckedInPaxFlightLeg(
              leg,
              paxEntry,
              codeTypeList,
              response.selectedSSRList,
              flight.logicalFlightID,
              mealsList,
            ),
          );
        }
      }

      if (notCheckedInLegs.length > 0) {
        const firstLeg = flight.legs[0];
        flights.push({
          origin: firstLeg.origin,
          destination: firstLeg.destination,
          sellingCarrier: firstLeg.sellingCarrier,
          flightNum: firstLeg.flightNum,
          flightClass: getClassValue(firstLeg),
          legs: notCheckedInLegs,
          logicalFlightID: flight.logicalFlightID ?? '',
        });
      }
    }

    if (flights.length > 0) {
      const countryList = selectCountries(useMasterDataStore.getState());
      const infantResPaxId = getInfantResPaxIdByParent(response.paxList, passenger.resPaxId);
      notCheckedInPassengers.push({
        isPrimaryPassenger: passenger.isPrimaryPassenger,
        paxId: String(passenger.resPaxId),
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        paxJourneyId: passenger.paxJourneyId,
        resPaxId: passenger.resPaxId,
        recordNumber: passenger.recordNumber,
        sprintRefId: passenger.sprintRefId,
        fullName: formatPassengerName(passenger.title, passenger.firstName, passenger.lastName),
        flights,
        associatedInfantID: resolveAssociatedInfantId(
          infantResPaxId,
          passenger.travelsWithPassengerId,
        ),
        passengerType: mapPassengerTypeToString(passenger.passengerType),
        sameDayReturn: response.flights.length > 1,
        contactInfo: passenger.contactInfo ?? [],
        apisInfo: mapApiApisInfoToUi(passenger.apisInfo),
        skywardId: passenger.fFNum ?? '',
        skywardTier: passenger.tier ?? '',
        emergencyContact:
          passenger.emergencyContact !== null
            ? mapEmergencyContactToDetails(passenger.emergencyContact, countryList)
            : undefined,
        isId90: response?.validationRules?.isId90,
        isApisOk: passenger.isApisOk,
        isGCCNational: passenger.isGCCNational,
        isEUNational: passenger.isEUNational,
        isAllowCheckInCount: counters.isAllowCheckInCount,
        checkedInFlightsCount: counters.checkedInFlightsCount,
        standByFlightsCount: counters.standByFlightsCount,
        standByID90: counters.standByID90,
        standByWaitlist: counters.standByWaitlist,
        leftBorderColor: counters.leftBorderColor,
        canSelectForCheckIn: counters.canSelect,
      });
    }
  }

  return notCheckedInPassengers;
}
